/**
 * Databricks Client Service
 * Frontend service to fetch analytics data from Databricks via backend
 */

import apiClient from './apiClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:5001';

/**
 * Helper to get auth headers
 */
async function getAuthHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const token = await apiClient.getAccessToken();
    headers['Authorization'] = `Bearer ${token}`;
  } catch (e) {
    console.warn('No auth token available for Databricks request');
  }
  return headers;
}

/**
 * Check Databricks connection status
 */
export async function getDatabricksStatus() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${BACKEND_URL}/api/databricks/status`, { headers });
  return response.json();
}

/**
 * Get current market prices (WTI, Henry Hub, basis differentials)
 */
export async function getMarketPrices() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${BACKEND_URL}/api/databricks/market-prices`, { headers });
  return response.json();
}

/**
 * Get operator performance metrics
 * @param {Object} filters - Optional filters { county, operator }
 */
export async function getOperatorMetrics(filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (filters.county) params.append('county', filters.county);
  if (filters.operator) params.append('operator', filters.operator);
  const queryString = params.toString();
  const url = `${BACKEND_URL}/api/databricks/operator-metrics${queryString ? `?${queryString}` : ''}`;
  const response = await fetch(url, { headers });
  return response.json();
}

/**
 * Get production summary by county
 * @param {Object} filters - Optional filters { county, operator }
 */
export async function getProductionSummary(filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (filters.county) params.append('county', filters.county);
  if (filters.operator) params.append('operator', filters.operator);
  const queryString = params.toString();
  const url = `${BACKEND_URL}/api/databricks/production/summary${queryString ? `?${queryString}` : ''}`;
  const response = await fetch(url, { headers });
  return response.json();
}

/**
 * Get production history for a specific well
 */
export async function getProductionHistory(wellApi) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${BACKEND_URL}/api/databricks/production/${encodeURIComponent(wellApi)}`, { headers });
  return response.json();
}

/**
 * Get production forecast for a specific well
 */
export async function getProductionForecast(wellApi) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${BACKEND_URL}/api/databricks/forecasts/${encodeURIComponent(wellApi)}`, { headers });
  return response.json();
}
